/**
 * English lexicons: typed loaders over the frozen seed data and the v3 additions
 * (ENGINE-SPEC §5). Static JSON imports so bundlers inline them; no I/O at run time.
 *
 * `seed-2.6/*.json` are written by `tools/extract-lexicons.ts` and are never hand-edited.
 * `prefilters.json` and `overrides.json` are v3 data kept apart from the frozen seed values.
 */

import ob01AiciteJson from '../v3/ob01-aicite.json';
import overridesJson from './overrides.json';
import prefiltersJson from './prefilters.json';
import brandReJson from './seed-2.6/brand-re.json';
import buildDirectivesJson from './seed-2.6/build-directives.json';
import chDs03Json from './seed-2.6/ch-ds03.json';
import chFm01Json from './seed-2.6/ch-fm01.json';
import chLx01Json from './seed-2.6/ch-lx01.json';
import chLx03Json from './seed-2.6/ch-lx03.json';
import chMt01Json from './seed-2.6/ch-mt01.json';
import chNa01Json from './seed-2.6/ch-na01.json';
import chOb01Json from './seed-2.6/ch-ob01.json';
import chPt01Json from './seed-2.6/ch-pt01.json';
import chPt02Json from './seed-2.6/ch-pt02.json';
import chRh03Json from './seed-2.6/ch-rh03.json';
import chSf01Json from './seed-2.6/ch-sf01.json';
import chSf03Json from './seed-2.6/ch-sf03.json';
import chSf04Json from './seed-2.6/ch-sf04.json';
import chSf05Json from './seed-2.6/ch-sf05.json';
import chSt03Json from './seed-2.6/ch-st03.json';
import chSt04Json from './seed-2.6/ch-st04.json';
import chTx01Json from './seed-2.6/ch-tx01.json';
import contractionReJson from './seed-2.6/contraction-re.json';
import contrastResJson from './seed-2.6/contrast-res.json';
import fnSegleanJson from './seed-2.6/fn-seglean.json';
import fnTokenizeJson from './seed-2.6/fn-tokenize.json';
import growthReJson from './seed-2.6/growth-re.json';
import hedgesJson from './seed-2.6/hedges.json';
import lexSwapsJson from './seed-2.6/lex-swaps.json';
import packProcessJson from './seed-2.6/pack-process.json';
import phraseResJson from './seed-2.6/phrase-res.json';
import phraseSwapsJson from './seed-2.6/phrase-swaps.json';
import phrasesJson from './seed-2.6/phrases.json';
import repairDirectivesJson from './seed-2.6/repair-directives.json';
import scaffoldReJson from './seed-2.6/scaffold-re.json';
import scaffoldsJson from './seed-2.6/scaffolds.json';
import tellsJson from './seed-2.6/tells.json';
import triadReJson from './seed-2.6/triad-re.json';
import tropeResJson from './seed-2.6/trope-res.json';

/** Embedded in every result and in the model JSON (ENGINE-SPEC §5, §8). */
export const lexiconVersion = 'seed-2.6+v3.2026.10';

export interface LexiconMeta {
	version: string;
	source: string;
	addedBy: string;
	rationale: string;
	inv?: string;
}

export interface RegexData {
	source: string;
	flags: string;
}

export interface WeightedWordsData extends LexiconMeta {
	count: number;
	entries: [string, number][];
}
export interface StringListData extends LexiconMeta {
	count: number;
	entries: string[];
}
export interface PairListData extends LexiconMeta {
	count: number;
	entries: [string, string][];
}
export interface RegexListData extends LexiconMeta {
	count: number;
	entries: RegexData[];
}
export interface PhraseReData extends LexiconMeta {
	count: number;
	entries: ({ phrase: string } & RegexData)[];
}
export interface SingleRegexData extends LexiconMeta {
	re: RegexData;
}
export interface DirectiveData extends LexiconMeta {
	vocabLimit: number;
	vocabSeparator: string;
	count: number;
	/** `when` lists the channel codes that trigger the line; empty means always. */
	entries: { when: string[]; text: string }[];
}
/** Named regex and list literals of one channel, function or the process pack. */
export interface NamedPatternsData extends LexiconMeta {
	channel?: string;
	patterns: Record<string, RegexData>;
	lists: Record<string, string[]>;
}
export interface PrefilterData extends LexiconMeta {
	/** Index-aligned with the regex list of the same name in `seed-2.6`. */
	lists: Record<string, string[][]>;
	/** By channel file, then pattern name. */
	channels: Record<string, Record<string, string[]>>;
}
/** Conservative rewrites of seed regexes that fail T-LEX-04 (candidate change C-LEX-BT). */
export interface OverridesData extends LexiconMeta {
	entries: {
		id: string;
		change: string;
		target: string;
		index?: number;
		pattern?: string;
		source: string;
		flags: string;
		reason: string;
	}[];
}
export interface Ob01AiciteData extends LexiconMeta {
	patterns: {
		id: string;
		kind: 'literal' | 'regex';
		pattern: string;
		flags: string;
		prefilter: string[];
	}[];
}

export type ChannelFile =
	| 'ch-lx01'
	| 'ch-lx03'
	| 'ch-pt01'
	| 'ch-pt02'
	| 'ch-rh03'
	| 'ch-st03'
	| 'ch-st04'
	| 'ch-na01'
	| 'ch-fm01'
	| 'ch-sf04'
	| 'ch-ds03'
	| 'ch-sf05'
	| 'ch-mt01'
	| 'ch-ob01'
	| 'ch-sf01'
	| 'ch-sf03'
	| 'ch-tx01';

export interface EnglishLexicons {
	lexiconVersion: string;
	/** INV-LEX-01 */ tells: WeightedWordsData;
	/** INV-LEX-02 */ phrases: StringListData;
	/** INV-LEX-03 */ phraseRes: PhraseReData;
	/** INV-LEX-04 */ scaffolds: StringListData;
	/** INV-LEX-05 */ hedges: StringListData;
	/** INV-LEX-06 */ contrastRes: RegexListData;
	/** INV-LEX-07 */ brandRe: SingleRegexData;
	/** INV-LEX-08 */ tropeRes: RegexListData;
	/** INV-LEX-09 */ growthRe: SingleRegexData;
	/** INV-LEX-10 */ triadRe: SingleRegexData;
	/** INV-LEX-11 */ scaffoldRe: SingleRegexData;
	/** INV-LEX-12 */ contractionRe: SingleRegexData;
	/** INV-LEX-13 */ lexSwaps: PairListData;
	/** INV-LEX-14 */ phraseSwaps: PairListData;
	/** INV-LEX-15 */ repairDirectives: StringListData;
	/** buildDirectives text, keyed by firing channel */ buildDirectives: DirectiveData;
	/** Regex and list literals inside the seed channel bodies. */
	channels: Record<ChannelFile, NamedPatternsData>;
	/** segLean (SG-01 lean) and tokenize (context builder) literals. */
	functions: { segLean: NamedPatternsData; tokenize: NamedPatternsData };
	/** Process-pack literals, including the PROC-08 FRAME_STOP list. */
	processPack: NamedPatternsData;
	/** v3 literal prefilters (not part of the frozen seed). */
	prefilters: PrefilterData;
	/** v3 conservative regex rewrites (C-LEX-BT), applied by the compiler. */
	overrides: OverridesData;
	/** v3 OB-01 chat-citation artefact patterns. */
	ob01Aicite: Ob01AiciteData;
}

const cast = <T>(x: unknown): T => x as T;

let cached: EnglishLexicons | undefined;

/** The whole English lexicon set (static, shared, not to be mutated by callers). */
export function loadEnglishLexicons(): EnglishLexicons {
	cached ??= {
		lexiconVersion,
		tells: cast(tellsJson),
		phrases: cast(phrasesJson),
		phraseRes: cast(phraseResJson),
		scaffolds: cast(scaffoldsJson),
		hedges: cast(hedgesJson),
		contrastRes: cast(contrastResJson),
		brandRe: cast(brandReJson),
		tropeRes: cast(tropeResJson),
		growthRe: cast(growthReJson),
		triadRe: cast(triadReJson),
		scaffoldRe: cast(scaffoldReJson),
		contractionRe: cast(contractionReJson),
		lexSwaps: cast(lexSwapsJson),
		phraseSwaps: cast(phraseSwapsJson),
		repairDirectives: cast(repairDirectivesJson),
		buildDirectives: cast(buildDirectivesJson),
		channels: {
			'ch-lx01': cast(chLx01Json),
			'ch-lx03': cast(chLx03Json),
			'ch-pt01': cast(chPt01Json),
			'ch-pt02': cast(chPt02Json),
			'ch-rh03': cast(chRh03Json),
			'ch-st03': cast(chSt03Json),
			'ch-st04': cast(chSt04Json),
			'ch-na01': cast(chNa01Json),
			'ch-fm01': cast(chFm01Json),
			'ch-sf04': cast(chSf04Json),
			'ch-ds03': cast(chDs03Json),
			'ch-sf05': cast(chSf05Json),
			'ch-mt01': cast(chMt01Json),
			'ch-ob01': cast(chOb01Json),
			'ch-sf01': cast(chSf01Json),
			'ch-sf03': cast(chSf03Json),
			'ch-tx01': cast(chTx01Json),
		},
		functions: { segLean: cast(fnSegleanJson), tokenize: cast(fnTokenizeJson) },
		processPack: cast(packProcessJson),
		prefilters: cast(prefiltersJson),
		overrides: cast(overridesJson),
		ob01Aicite: cast(ob01AiciteJson),
	};
	return cached;
}
